/* Model personality — persona (option B, "persona row"). A 44px row naming the
   current persona, or "Custom" once either chat style is edited; "Change ›"
   opens a picker with a preview of each preset. Loading one replaces both chat
   styles and the normal-chat emojis, with an Undo banner. */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CHAT_STYLE_PRESETS, type ChatStylePreset } from '../../chatStylePresets';
import { Btn, Icon } from '../kit';
import { C } from '../tokens';
import { font as f } from '../style';

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const iconBox = { width: 28, height: 28, flex: 'none', borderRadius: 7, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;

/** `current` = the preset the chat styles match; `base` = the one they were last loaded from. */
export function PersonaRow({ current, base, onOpen }: { current?: ChatStylePreset; base?: ChatStylePreset; onOpen: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const name = current ? tc(`modelInfo.stylePresets.${current.id}.name`) : t('mp.persona.custom');
  const desc = current
    ? t('mp.persona.sets', { desc: tc(`modelInfo.stylePresets.${current.id}.desc`) })
    : base ? t('mp.persona.customBased', { name: tc(`modelInfo.stylePresets.${base.id}.name`) }) : t('mp.persona.customDesc');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 44, padding: '0 8px 0 14px', boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <Icon name={current ? current.icon : 'edit-2'} size={14} color={C.purpleLt} style={{ width: 16 }} />
      <span style={{ font: f(500, 13), color: C.sub, flex: 'none' }}>{t('mp.persona.label')}</span>
      <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap', flex: 'none' }}>{name}</span>
      <span style={{ ...ellipsis, flex: '1 1 auto', minWidth: 0, font: f(400, 12), color: C.muted }}>{desc}</span>
      <Btn kind="muted" onClick={onOpen} style={{ color: C.purplePale }}>
        {t('mp.persona.change')}
        <Icon name="chevron-right" size={13} />
      </Btn>
    </div>
  );
}

export function UndoBanner({ label, onUndo, onDismiss }: { label: string; onUndo: () => void; onDismiss: () => void }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px 7px 12px', background: 'color-mix(in srgb, var(--om-accent) 10%, transparent)', border: `1px solid ${C.tintLine}`, borderRadius: 8, font: f(400, 12), color: C.purplePale }}>
      <Icon name="check-circle" size={13} />
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      <button type="button" onClick={onUndo} className="cpt-hover" style={{ height: 24, flex: 'none', padding: '0 10px', border: 0, borderRadius: 5, background: 'transparent', color: C.text, font: f(600, 12), cursor: 'pointer' }}>{t('mp.persona.undo')}</button>
      <button type="button" onClick={onDismiss} title={t('mp.persona.dismiss')} className="cpt-hover-text" style={{ width: 24, height: 24, flex: 'none', padding: 0, border: 0, borderRadius: 5, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{children}</span>
);
const Block = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: '10px 12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, font: f(400, 12, 1.55), color: C.body, whiteSpace: 'pre-wrap' }}>{children}</div>
);

/** Full-window picker: preset list on the left, preview of the highlighted one
 *  on the right. Closes with Cancel, × or Esc (not on a backdrop click). */
export function PersonaPicker({ current, custom, onUse, onClose }: { current?: ChatStylePreset; custom: boolean; onUse: (p: ChatStylePreset) => void; onClose: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const [pv, setPv] = useState<ChatStylePreset>(() => current ?? CHAT_STYLE_PRESETS[0]!);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  const name = (p: ChatStylePreset) => tc(`modelInfo.stylePresets.${p.id}.name`);
  const desc = (p: ChatStylePreset) => tc(`modelInfo.stylePresets.${p.id}.desc`);
  const isCur = pv.id === current?.id;
  return createPortal(
    <div className="cpt-root" style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'color-mix(in srgb, var(--om-page) 70%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div role="dialog" aria-modal="true" aria-label={t('mp.persona.choose')} style={{ width: 'min(860px,100%)', height: 'min(560px,100%)', display: 'flex', flexDirection: 'column', background: C.surface, border: `1px solid ${C.borderHi}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,.5)' }}>
        <div style={{ height: 48, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '0 10px 0 16px', borderBottom: `1px solid ${C.divider}` }}>
          <span style={{ font: f(600, 14), color: C.text, flex: 1 }}>{t('mp.persona.choose')}</span>
          <button type="button" onClick={onClose} title={t('close')} className="cpt-hover-row" style={{ width: 28, height: 28, padding: 0, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="x" size={15} />
          </button>
        </div>
        <div className="cpt-pp-body" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <div className="cpt-thin-scroll cpt-pp-list" style={{ width: 260, flex: 'none', borderRight: `1px solid ${C.divider}`, padding: 8, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', boxSizing: 'border-box' }}>
            {CHAT_STYLE_PRESETS.map((p) => {
              const a = p.id === pv.id;
              return (
                <button key={p.id} type="button" onClick={() => setPv(p)} className={a ? 'cpt-pp-item' : 'cpt-hover cpt-pp-item'} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, textAlign: 'left', background: a ? C.tint16 : 'transparent', border: 0, borderRadius: 7, cursor: 'pointer' }}>
                  <span style={iconBox}><Icon name={p.icon} size={14} /></span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
                    <span style={{ ...ellipsis, font: f(600, 12.5), color: C.text }}>{name(p)}</span>
                    <span style={{ ...ellipsis, font: f(400, 11), color: C.sub }}>{desc(p).split(/ · |, | — /)[0]}</span>
                  </span>
                  {p.id === current?.id && <Icon name="check" size={13} color={C.purpleLt} />}
                </button>
              );
            })}
          </div>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ font: f(700, 16), color: C.text }}>{name(pv)}</span>
                <span style={{ font: f(400, 12), color: C.sub }}>{desc(pv)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>{t('mp.persona.sounds')}</Label>
                <div style={{ alignSelf: 'flex-start', maxWidth: 420, padding: '8px 12px', background: C.segActive, borderRadius: '12px 12px 12px 4px', font: f(400, 12.5, 1.45), color: C.text }}>{t(`mp.samples.${pv.id}`)}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><Label>{t('mp.persona.normal')}</Label><Block>{pv.normal}</Block></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}><Label>{t('mp.persona.hot')}</Label><Block>{pv.hot}</Block></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Label>{t('mp.persona.emojis')}</Label><span style={{ fontSize: 15, letterSpacing: 2 }}>{pv.emojis.join(' ')}</span></div>
            </div>
            <div style={{ height: 52, flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px', borderTop: `1px solid ${C.divider}` }}>
              <span style={{ flex: 1, minWidth: 0, font: f(400, 11.5), color: custom ? C.purplePale : C.muted }}>{custom ? t('mp.persona.warnCustom') : t('mp.persona.warn')}</span>
              <Btn kind="muted" height={30} onClick={onClose} style={{ color: C.body }}>{t('cancel')}</Btn>
              <Btn kind="primary" height={30} disabled={isCur} onClick={() => onUse(pv)}>{isCur ? t('mp.persona.current') : t('mp.persona.use', { name: name(pv) })}</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
